import { useLayoutEffect, useRef, useState } from 'react'
import { cn } from '@/lib/utils/helpers'

/**
 * Canonical copy for table/list empty state when the user can change search or filters
 * (parity with legacy admin / activity logs).
 */
export const NO_RECORDS_SEARCH_FILTER_MESSAGE =
  'No Records Found. Please try a different Search or Filter from the drop-downs above.'

const PANEL_FRAME =
  'rounded-md border border-slate-200 bg-white shadow-sm flex min-h-[12rem] items-center justify-center px-4 py-8 sm:px-6 sm:py-10'
const MESSAGE_TEXT =
  'text-center font-bold text-[#0f172a] text-[15px] leading-relaxed max-w-3xl mx-auto'

type PanelProps = {
  /** Merged with the outer frame; e.g. `mt-4` for spacing below toolbars */
  className?: string
  'data-testid'?: string
}

/** Full-width block shown instead of a table (e.g. company/property activity). */
export function TableSearchFilterEmptyPanel({ className, 'data-testid': dataTestId }: PanelProps) {
  return (
    <div data-testid={dataTestId} className={cn(PANEL_FRAME, 'w-full', className)}>
      <p className={MESSAGE_TEXT}>{NO_RECORDS_SEARCH_FILTER_MESSAGE}</p>
    </div>
  )
}

type TableRowProps = {
  colSpan: number
  /** `data-testid` on the `<td>` (e.g. *-table-empty-message). */
  'data-testid'?: string
  /** Optional `data-testid` on the `<tr>` (e.g. *-table-empty-row). */
  rowDataTestId?: string
  className?: string
  /** Extra classes on the inner white panel (e.g. `m-1` inside dense tables) */
  innerClassName?: string
}

function findHorizontalScrollParent(start: HTMLElement | null): HTMLElement | null {
  let node: HTMLElement | null = start
  while (node) {
    const { overflowX } = getComputedStyle(node)
    if (
      (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') &&
      node.scrollWidth > node.clientWidth
    ) {
      return node
    }
    // Prefer any declared horizontal scroll container (even when not overflowing yet).
    if (overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') {
      return node
    }
    node = node.parentElement
  }
  return null
}

/**
 * A single table row: `<tr><td colSpan=…>…` with the same bordered message panel as
 * `TableSearchFilterEmptyPanel` so desktop tables match card-style empty UIs.
 *
 * The panel is `position: sticky; left: 0` and sized to the scroll viewport so the
 * message stays visible when the table is wider than the screen.
 */
export function TableSearchFilterEmptyTableRow({
  colSpan,
  'data-testid': dataTestId,
  rowDataTestId,
  className,
  innerClassName,
}: TableRowProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const [viewportWidth, setViewportWidth] = useState<number | null>(null)

  useLayoutEffect(() => {
    const panel = panelRef.current
    if (!panel) return

    const scrollParent = findHorizontalScrollParent(panel)
    if (!scrollParent) return

    const syncWidth = () => {
      // Account for panel horizontal margins (m-2 / sm:m-3 ≈ 16–24px each side).
      const style = getComputedStyle(panel)
      const marginX =
        (Number.parseFloat(style.marginLeft) || 0) +
        (Number.parseFloat(style.marginRight) || 0)
      setViewportWidth(Math.max(0, scrollParent.clientWidth - marginX))
    }

    syncWidth()
    const observer = new ResizeObserver(syncWidth)
    observer.observe(scrollParent)
    window.addEventListener('resize', syncWidth)
    return () => {
      observer.disconnect()
      window.removeEventListener('resize', syncWidth)
    }
  }, [])

  return (
    <tr className={className} data-testid={rowDataTestId}>
      <td colSpan={colSpan} className="p-0 align-top" data-testid={dataTestId}>
        <div
          ref={panelRef}
          className={cn(
            PANEL_FRAME,
            'm-2 box-border sm:m-3',
            'sticky left-0 z-[1]',
            viewportWidth == null && 'w-full',
            innerClassName,
          )}
          style={viewportWidth != null ? { width: viewportWidth } : undefined}
        >
          <p className={MESSAGE_TEXT}>{NO_RECORDS_SEARCH_FILTER_MESSAGE}</p>
        </div>
      </td>
    </tr>
  )
}

/** Grid / mobile list empty slot (e.g. `col-span-full`). */
export function TableSearchFilterEmptyGridSlot({ className, 'data-testid': dataTestId }: PanelProps) {
  return (
    <div
      data-testid={dataTestId}
      className={cn('col-span-full', PANEL_FRAME, 'w-full sm:px-6', className)}
    >
      <p className={MESSAGE_TEXT}>{NO_RECORDS_SEARCH_FILTER_MESSAGE}</p>
    </div>
  )
}
